import { Card } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, CardActionArea, useTheme } from "@mui/material";
import { InternalLink } from "@src/appV2/lib";
import { isAfter, parseISO } from "date-fns";

import { IconButton } from "../../components/IconButton";
import { OverlappingShiftWorkersAvatars } from "../../WorkWithFriends/components/OverlappingShiftWorkersAvatars";
import { type WorkersForOverlappingShiftSlots } from "../../WorkWithFriends/hooks/useGetWorkersForOverlappingShiftSlots";
import { WorkerShiftCardFooter } from "../Card/Footer";
import { BookingCardContentWrapper } from "../CardContentWrapper";
import { useShiftDurationWithBreak } from "../hooks/useShiftDurationWithBreak";
import { ShiftCardSummaryInfoDeprecated } from "../Open/CardSummaryInfoDeprecated";
import { type TimeSlot } from "../Open/types";
import { BookingCardSummaryWrapper } from "../SummaryWrapper";
import { TimeSlotIndicator } from "../TimeSlotIndicator";
import { CancellationPenaltyFooter } from "./CancellationPenaltyFooter/CancellationPenaltyFooter";

export interface WorkerShiftCardProps {
  href?: string;
  shiftId: string;
  shiftTimeSlot: TimeSlot;
  shiftStart: string;
  shiftEnd: string;
  shiftQualificationName: string;
  shiftQualification?: string;
  shiftHourlyPay?: string;
  shiftTotalPay?: string;
  shiftTotalWithBoostPay?: string;
  workersForOverlappingShiftSlot?: WorkersForOverlappingShiftSlots;
  workplaceId: string;
  workplaceName: string;
  workplaceRequiresLunchBreak: boolean;
  workplaceTimezone: string;
  isBookingRequest?: boolean;
}

export function WorkerShiftCard(props: WorkerShiftCardProps) {
  const {
    workplaceId,
    workplaceName,
    workplaceTimezone,
    shiftId,
    shiftTimeSlot,
    shiftStart,
    shiftEnd,
    shiftQualificationName,
    shiftQualification,
    shiftHourlyPay,
    shiftTotalPay,
    shiftTotalWithBoostPay,
    workersForOverlappingShiftSlot,
    workplaceRequiresLunchBreak,
    href,
    isBookingRequest,
  } = props;

  const theme = useTheme();

  const { totalDurationInHours, mandatoryBreakDurationInMinutes, requiresBreak } =
    useShiftDurationWithBreak({
      startDate: parseISO(shiftStart),
      endDate: parseISO(shiftEnd),
      workplaceId,
      requiresLunchBreak: workplaceRequiresLunchBreak,
    });
  const showOverlappingShiftWorkersAvatars =
    isDefined(workersForOverlappingShiftSlot) &&
    (workersForOverlappingShiftSlot.friends.length > 0 ||
      workersForOverlappingShiftSlot.recentColleagues.length > 0 ||
      workersForOverlappingShiftSlot.otherWorkers.length > 0);

  const shiftStartDate = parseISO(shiftStart);

  const cardContent = (
    <BookingCardContentWrapper>
      <BookingCardSummaryWrapper>
        <TimeSlotIndicator timeSlot={shiftTimeSlot} />

        <ShiftCardSummaryInfoDeprecated
          dateRange={{ startDate: shiftStartDate, endDate: parseISO(shiftEnd) }}
          timezone={workplaceTimezone}
          deviceTimezone={new Intl.DateTimeFormat().resolvedOptions().timeZone}
          durationInHours={totalDurationInHours}
          breakDurationInMinutes={mandatoryBreakDurationInMinutes}
          requiresBreak={requiresBreak}
          workplaceName={workplaceName}
          useNowForStartDate={isAfter(new Date(), shiftStartDate)}
        />
      </BookingCardSummaryWrapper>

      <WorkerShiftCardFooter
        hourlyPay={shiftHourlyPay}
        totalPay={shiftTotalPay}
        totalWithBoostPay={shiftTotalWithBoostPay}
        qualificationText={shiftQualificationName}
      />

      {showOverlappingShiftWorkersAvatars && (
        <Box sx={{ position: "absolute", right: theme.spacing(6), top: theme.spacing(3) }}>
          <OverlappingShiftWorkersAvatars
            overlappingShiftWorkers={workersForOverlappingShiftSlot}
            profileImageSizeInPx={32}
          />
        </Box>
      )}
      {!showOverlappingShiftWorkersAvatars && isDefined(href) && (
        <IconButton
          aria-label="Go to shift page"
          size="xSmall"
          variant="outlined"
          iconType="chevron-right"
          sx={{ position: "absolute", right: theme.spacing(6), top: theme.spacing(6) }}
        />
      )}
    </BookingCardContentWrapper>
  );

  return (
    <Card outlined variant="quaternary" sx={{ flexGrow: 1 }}>
      {isDefined(href) ? (
        <CardActionArea component={InternalLink} to={href}>
          {cardContent}
        </CardActionArea>
      ) : (
        cardContent
      )}
      {!isBookingRequest && (
        <Box
          sx={{
            backgroundColor: theme.palette.background.tertiary,
            borderTop: `1px solid ${theme.palette.border?.primary ?? theme.palette.divider}`,
            "&:empty": { display: "none" },
            paddingX: 6,
            paddingY: 4,
          }}
        >
          <CancellationPenaltyFooter
            shiftId={shiftId}
            shiftStart={shiftStart}
            workplaceId={workplaceId}
            qualification={shiftQualification}
          />
        </Box>
      )}
    </Card>
  );
}
